/**
 * Utility for Staff Chat Notification Tones (Incoming & Outgoing)
 * Supports Web Audio API synthesis with HTML5 Audio data URI fallback,
 * browser autoplay unlock, and WebSocket frame deduplication.
 */

let sharedAudioContext = null;
let incomingWavDataUri = null;
let outgoingWavDataUri = null;

// Track message IDs and timestamps to prevent duplicate chimes across components
const playedIncomingMessageIds = new Set();
let lastIncomingToneTime = 0;
let lastOutgoingToneTime = 0;

/**
 * Generate a clean 16-bit PCM WAV base64 data URI for incoming chime
 */
const getIncomingWavDataUri = () => {
  if (incomingWavDataUri) return incomingWavDataUri;
  try {
    const sampleRate = 22050;
    const duration = 0.36;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);

    const writeString = (offset, str) => {
      for (let i = 0; i < str.length; i += 1) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + numSamples * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, 1, true); // Mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, numSamples * 2, true);

    for (let i = 0; i < numSamples; i += 1) {
      const t = i / sampleRate;
      let sample = 0;
      if (t < 0.12) {
        const env = Math.exp(-t * 22);
        sample = Math.sin(2 * Math.PI * 659.25 * t) * env * 0.45;
      } else {
        const t2 = t - 0.11;
        const env2 = Math.exp(-t2 * 10);
        sample = Math.sin(2 * Math.PI * 987.77 * t2) * env2 * 0.5;
      }
      const intSample = Math.max(-32768, Math.min(32767, Math.floor(sample * 32767)));
      view.setInt16(44 + i * 2, intSample, true);
    }

    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    incomingWavDataUri = `data:audio/wav;base64,${btoa(binary)}`;
    return incomingWavDataUri;
  } catch (e) {
    return null;
  }
};

/**
 * Generate a clean 16-bit PCM WAV base64 data URI for outgoing pop
 */
const getOutgoingWavDataUri = () => {
  if (outgoingWavDataUri) return outgoingWavDataUri;
  try {
    const sampleRate = 22050;
    const duration = 0.08;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);

    const writeString = (offset, str) => {
      for (let i = 0; i < str.length; i += 1) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + numSamples * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, numSamples * 2, true);

    for (let i = 0; i < numSamples; i += 1) {
      const t = i / sampleRate;
      const freq = 750 - (750 - 320) * (t / duration);
      const env = (1 - (t / duration)) * Math.min(1, t / 0.008);
      const sample = Math.sin(2 * Math.PI * freq * t) * env * 0.4;
      const intSample = Math.max(-32768, Math.min(32767, Math.floor(sample * 32767)));
      view.setInt16(44 + i * 2, intSample, true);
    }

    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    outgoingWavDataUri = `data:audio/wav;base64,${btoa(binary)}`;
    return outgoingWavDataUri;
  } catch (e) {
    return null;
  }
};

/**
 * Get or create shared AudioContext
 */
export const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  if (!sharedAudioContext) {
    try {
      sharedAudioContext = new AudioContextClass();
    } catch (e) {
      return null;
    }
  }

  if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().catch(() => {});
  }

  return sharedAudioContext;
};

/**
 * Unlocks Web Audio on user gesture
 */
export const unlockAudio = () => {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  try {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch (e) {
    // Ignore unlock errors
  }
};

// Listen to early user interaction to unlock audio
if (typeof window !== 'undefined') {
  const handleInteraction = () => {
    unlockAudio();
  };

  ['pointerdown', 'click', 'keydown', 'touchstart'].forEach((event) => {
    window.addEventListener(event, handleInteraction, { passive: true, capture: true });
  });
}

/**
 * Play HTML5 Audio fallback
 */
const playAudioElementFallback = (dataUri) => {
  if (!dataUri || typeof window === 'undefined') return;
  try {
    const audio = new Audio(dataUri);
    audio.volume = 0.6;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  } catch (e) {
    // Ignore autoplay restriction errors
  }
};

const WHATSAPP_NOTIFICATION_SOUND_URL = `${process.env.PUBLIC_URL || ''}/sounds/whatsapp-notification.mp3`;

/**
 * Play a pleasant, crisp incoming message chime
 * Plays the official WhatsApp notification MP3, with Web Audio chime fallback.
 * @param {string|number} [messageId] Optional message ID to prevent duplicate chimes
 */
export const playIncomingMessageTone = (messageId) => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (messageId && playedIncomingMessageIds.has(String(messageId))) {
    return;
  }
  if (now - lastIncomingToneTime < 350) {
    return;
  }

  if (messageId) {
    playedIncomingMessageIds.add(String(messageId));
    if (playedIncomingMessageIds.size > 200) {
      const first = playedIncomingMessageIds.values().next().value;
      playedIncomingMessageIds.delete(first);
    }
  }
  lastIncomingToneTime = now;

  const playSynthesizedFallback = () => {
    try {
      const ctx = getAudioContext();
      if (ctx) {
        const playChime = () => {
          try {
            const nowTime = ctx.currentTime;
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(659.25, nowTime);
            gain1.gain.setValueAtTime(0.001, nowTime);
            gain1.gain.linearRampToValueAtTime(0.35, nowTime + 0.02);
            gain1.gain.exponentialRampToValueAtTime(0.001, nowTime + 0.15);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.start(nowTime);
            osc1.stop(nowTime + 0.16);

            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(987.77, nowTime + 0.11);
            gain2.gain.setValueAtTime(0.001, nowTime + 0.11);
            gain2.gain.linearRampToValueAtTime(0.4, nowTime + 0.13);
            gain2.gain.exponentialRampToValueAtTime(0.001, nowTime + 0.38);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.start(nowTime + 0.11);
            osc2.stop(nowTime + 0.39);
          } catch (e) {
            playAudioElementFallback(getIncomingWavDataUri());
          }
        };

        if (ctx.state === 'suspended') {
          ctx.resume().then(playChime).catch(() => {
            playAudioElementFallback(getIncomingWavDataUri());
          });
        } else {
          playChime();
        }
      } else {
        playAudioElementFallback(getIncomingWavDataUri());
      }
    } catch (e) {
      playAudioElementFallback(getIncomingWavDataUri());
    }
  };

  // Primary: Play official WhatsApp notification mp3 audio
  try {
    const audio = new Audio(WHATSAPP_NOTIFICATION_SOUND_URL);
    audio.volume = 0.85;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        playSynthesizedFallback();
      });
    }
  } catch (error) {
    playSynthesizedFallback();
  }
};

/**
 * Play a clean, subtle outgoing message pop (sent sound)
 */
export const playOutgoingMessageTone = () => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (now - lastOutgoingToneTime < 200) {
    return;
  }
  lastOutgoingToneTime = now;

  let playedViaWebAudio = false;

  try {
    const ctx = getAudioContext();
    if (ctx) {
      const playPop = () => {
        try {
          const nowTime = ctx.currentTime;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(750, nowTime);
          osc.frequency.exponentialRampToValueAtTime(320, nowTime + 0.06);

          gain.gain.setValueAtTime(0.001, nowTime);
          gain.gain.linearRampToValueAtTime(0.28, nowTime + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.001, nowTime + 0.07);

          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(nowTime);
          osc.stop(nowTime + 0.08);
        } catch (e) {
          playAudioElementFallback(getOutgoingWavDataUri());
        }
      };

      if (ctx.state === 'suspended') {
        ctx.resume().then(playPop).catch(() => {
          playAudioElementFallback(getOutgoingWavDataUri());
        });
      } else {
        playPop();
      }
      playedViaWebAudio = true;
    }
  } catch (error) {
    playedViaWebAudio = false;
  }

  if (!playedViaWebAudio) {
    playAudioElementFallback(getOutgoingWavDataUri());
  }
};

/**
 * Backward compatibility alias
 */
export const playStaffChatMessageTone = () => {
  playIncomingMessageTone();
};
